import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Tooltip,
  Stack,
  TextField,
  useMediaQuery,
  CircularProgress,
  MenuItem,
  Select,
} from "@mui/material";
import { ThemeProvider, createTheme, alpha } from "@mui/material/styles";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import API_BASE_URL from "../../utils/api";

// ---------------------------------------------------------------------

const SAMPLE_TOPICS = [
  {
    id: "t1",
    title: "Sample Topic",
    subtopics: [{ id: "s1", title: "Sample PDF", pdfUrl: "/pdfs/sample.pdf" }],
  },
];

function bytesToLabel(bytes) {
  if (!bytes && bytes !== 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(1)} ${units[i]}`;
}

function normalizeTopics(data) {
  if (!Array.isArray(data)) return SAMPLE_TOPICS;
  return data.map((t) => {
    const subs = Array.isArray(t.subtopics)
      ? t.subtopics
      : Array.isArray(t.items)
        ? t.items
        : [];
    return {
      id: t.id || t.slug || t.title || String(Math.random()),
      title: t.title || t.name || "Untitled Topic",
      subtopics: subs
        .map((s) => ({
          id: s.id || s.key || s.pdfUrl || s.url || String(Math.random()),
          title: s.title || s.name || "Untitled",
          pdfUrl: s.pdfUrl || s.url || "",
          sizeBytes: s.sizeBytes ?? s.size,
          updatedAt: s.updatedAt || s.lastModified,
        }))
        .filter((s) => !!s.pdfUrl),
    };
  });
}

// ---------------------------------------------------------------------

const PdfTutorials = ({
  title = "Digital Humanities Tutorials",
  topics: initialTopics,
  fetchUrl,
  accent = "#FFD700",
}) => {
  useEffect(() => {
    document.title = "Digital Humanities Tutorials";
  }, []);
  const isMobile = useMediaQuery("(max-width:900px)");

  const theme = createTheme({
    palette: { primary: { main: accent, contrastText: "#111" } },
    shape: { borderRadius: 12 },
  });

  const [topics, setTopics] = useState(initialTopics || SAMPLE_TOPICS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isReady, setIsReady] = useState(false);

  const effectiveUrl = fetchUrl || `${API_BASE_URL}/tutorials?signed=1`;
  useEffect(() => {
    document.title = "Digital Humanities Tutorials - MS Digital Hub";
  }, []);
  useEffect(() => {
    let canceled = false;
    const controller = new AbortController();

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(effectiveUrl, {
          signal: controller.signal,
        });
        if (!canceled) {
          const normalized = normalizeTopics(res.data);
          setTopics(normalized);
          const hasReal =
            Array.isArray(normalized) &&
            normalized.some(
              (t) => Array.isArray(t.subtopics) && t.subtopics.length > 0
            );
          setIsReady(hasReal);
        }
      } catch (e) {
        if (!canceled) {
          setError(
            e?.response?.data?.error ||
              e?.message ||
              "Failed to fetch tutorials"
          );
          setTopics(SAMPLE_TOPICS);
        }
      } finally {
        if (!canceled) setLoading(false);
      }
    })();

    return () => {
      canceled = true;
      controller.abort();
    };
  }, [effectiveUrl]);

  const [openTopicId, setOpenTopicId] = useState(null);
  const [search, setSearch] = useState("");
  const searchRef = useRef(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedSubtopic, setSelectedSubtopic] = useState(null);

  useEffect(() => {
    if (topics && topics.length) {
      const t = topics[0];
      const s = t?.subtopics?.[0] || null;
      setSelectedTopic(t);
      setSelectedSubtopic(s);
      setOpenTopicId(t.id);
    }
  }, [topics]);

  const filteredTopics = useMemo(() => {
    if (!search.trim()) return topics || [];
    const q = search.toLowerCase();
    return (topics || [])
      .map((t) => ({
        ...t,
        subtopics: (t.subtopics || []).filter((s) =>
          `${t.title} ${s.title}`.toLowerCase().includes(q)
        ),
      }))
      .filter((t) => (t.subtopics || []).length > 0);
  }, [topics, search]);

  const toggleTopic = (t) => {
    setOpenTopicId((prev) => (prev === t.id ? null : t.id));
    setSelectedTopic(t);
  };

  const selectSubtopic = (t, s) => {
    setSelectedTopic(t);
    setSelectedSubtopic(s);
  };

  const DL_COUNT_KEY = (id) => `pdf:downloads:${id}`;

  const glass = (opacity = 0.6) => ({
    bgcolor: alpha("#ffffff", opacity),
    backdropFilter: "saturate(150%) blur(8px)",
    boxShadow: `0 8px 24px ${alpha("#000", 0.06)}`,
    border: `1px solid ${alpha("#000", 0.06)}`,
  });

  // ---------------------------------------------------------------------
  // Compact dropdown for mobile instead of drawer
  const MobileSelector = () => (
    <Stack spacing={2} sx={{ mb: 3 }}>
      <Box>
        <Typography
          variant="subtitle2"
          sx={{ mb: 0.5, color: "#555", fontWeight: 600 }}
        >
          Select Topic
        </Typography>
        <Select
          size="small"
          fullWidth
          value={selectedTopic?.id || ""}
          onChange={(e) => {
            const topic = topics.find((t) => t.id === e.target.value);
            if (topic) {
              setSelectedTopic(topic);
              setSelectedSubtopic(topic.subtopics?.[0] || null);
            }
          }}
          displayEmpty
        >
          <MenuItem disabled value="">
            <em>Choose a topic</em>
          </MenuItem>
          {topics.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.title}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box>
        <Typography
          variant="subtitle2"
          sx={{ mb: 0.5, color: "#555", fontWeight: 600 }}
        >
          Select Subtopic
        </Typography>
        <Select
          size="small"
          fullWidth
          value={selectedSubtopic?.id || ""}
          onChange={(e) => {
            const sub = selectedTopic?.subtopics?.find(
              (s) => s.id === e.target.value
            );
            if (sub) setSelectedSubtopic(sub);
          }}
          displayEmpty
          disabled={!selectedTopic}
        >
          <MenuItem disabled value="">
            <em>
              {!selectedTopic ? "Choose topic first" : "Choose a subtopic"}
            </em>
          </MenuItem>
          {(selectedTopic?.subtopics || []).map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.title}
            </MenuItem>
          ))}
        </Select>
      </Box>
    </Stack>
  );

  const RightHeader = () => (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "flex-start", sm: "center" }}
      spacing={1}
      sx={{ mb: 2 }}
    >
      <Box>
        <Typography variant="h5" fontWeight={500}>
          {selectedTopic?.title} — {selectedSubtopic?.title}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Tooltip title="Open in new tab">
          <span>
            <IconButton
              disabled={!selectedSubtopic?.pdfUrl}
              onClick={() => window.open(selectedSubtopic.pdfUrl, "_blank")}
            >
              <OpenInNewIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Download">
          <span>
            <IconButton
              disabled={!selectedSubtopic?.pdfUrl}
              onClick={() => {
                if (!selectedSubtopic?.id) return;
                const key = DL_COUNT_KEY(selectedSubtopic.id);
                const n = Number(localStorage.getItem(key) || 0) + 1;
                localStorage.setItem(key, String(n));
                window.location.href = selectedSubtopic.pdfUrl;
              }}
            >
              <DownloadIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );

  // ---------------------------------------------------------------------

  return (
    <Box
      sx={{
        width: "auto",
        margin: "0 auto",
        padding: { xs: "20px", sm: "30px", md: "1px 80px" },
        backgroundColor: "#ffffff",
        borderRadius: "8px",
      }}
    >
      <ThemeProvider theme={theme}>
        <Typography
          variant="h4"
          sx={{ marginBottom: "30px", textAlign: "center" }}
        >
          {title}
        </Typography>

        <Box
          id="dh-tutorials-root"
          data-ready={isReady ? "1" : "0"}
          sx={{ display: "flex", flexDirection: isMobile ? "column" : "row" }}
        >
          {/* Sidebar (only on desktop) */}
          {!isMobile && (
            <Box
              sx={{
                width: 360,
                borderRight: `1px solid ${alpha("#000", 0.06)}`,
                bgcolor: alpha("#fff", 0.7),
                backdropFilter: "blur(6px)",
                p: 0,
              }}
            >
              <Box sx={{ width: 340, p: 2 }}>
                <TextField
                  inputRef={searchRef}
                  size="small"
                  placeholder="Search"
                  fullWidth
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ mb: 1.5 }}
                />
                <List dense>
                  {(filteredTopics || []).slice(0, 6).map((t) => {
                    const isOpen = openTopicId === t.id;
                    return (
                      <Box key={t.id} sx={{ mb: 0.5 }}>
                        <ListItemButton
                          onClick={() => toggleTopic(t)}
                          selected={selectedTopic?.id === t.id}
                          sx={{ borderRadius: 2, ...glass(0.7) }}
                        >
                          {isOpen ? (
                            <FolderOpenIcon fontSize="small" />
                          ) : (
                            <FolderIcon fontSize="small" />
                          )}
                          <ListItemText
                            sx={{ ml: 1 }}
                            primaryTypographyProps={{ fontWeight: 500 }}
                            primary={t.title}
                          />
                          {isOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                        </ListItemButton>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <List dense sx={{ pl: 4, mt: 0.5 }}>
                            {t.subtopics?.map((s) => {
                              const active = selectedSubtopic?.id === s.id;
                              return (
                                <ListItemButton
                                  key={s.id}
                                  onClick={() => selectSubtopic(t, s)}
                                  sx={{
                                    my: 0.5,
                                    borderRadius: 2,
                                    ...glass(active ? 0.9 : 0.65),
                                    border: `1px solid ${active ? accent : alpha("#000", 0.08)}`,
                                  }}
                                >
                                  <DescriptionIcon fontSize="small" />
                                  <ListItemText
                                    sx={{ ml: 1 }}
                                    primary={
                                      <Typography
                                        sx={{ fontWeight: active ? 700 : 500 }}
                                      >
                                        {s.title}
                                      </Typography>
                                    }
                                    secondary={
                                      s.sizeBytes
                                        ? bytesToLabel(s.sizeBytes)
                                        : undefined
                                    }
                                  />
                                </ListItemButton>
                              );
                            })}
                          </List>
                        </Collapse>
                      </Box>
                    );
                  })}
                </List>
              </Box>
            </Box>
          )}

          {/* Main content */}
          <Box sx={{ flex: 1, p: { xs: 2, md: 3 } }}>
            {isMobile && <MobileSelector />}
            <RightHeader />
            <Box
              sx={{
                p: 2,
                border: `1px solid ${alpha("#000", 0.06)}`,
                borderRadius: 0.5,
                ...glass(0.8),
              }}
            >
              {loading ? (
                <Stack alignItems="center" py={6}>
                  <CircularProgress color="primary" />
                  <Typography variant="body2" mt={1}>
                    Loading topics…
                  </Typography>
                </Stack>
              ) : error ? (
                <Typography color="error">{error}</Typography>
              ) : !selectedSubtopic?.pdfUrl ? (
                <Typography>Select a subtopic to preview its PDF.</Typography>
              ) : (
                <Box
                  sx={{
                    height: "calc(100vh - 220px)",
                    borderRadius: 0,
                    overflow: "hidden",
                    border: `1px solid ${alpha("#000", 0.06)}`,
                  }}
                >
                  <iframe
                    title="PDF Viewer"
                    src={selectedSubtopic.pdfUrl}
                    style={{ width: "100%", height: "100%", border: 0 }}
                    allow="fullscreen"
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </ThemeProvider>
    </Box>
  );
};

export default PdfTutorials;
